import { useEffect, useMemo, useState } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import type { SeasonContext } from '../lib/seasonContext.ts';
import { useMe } from '../lib/me.tsx';
import { fetchRecap, generateRecap, type Recap } from '../lib/api.ts';

/**
 * The weekly trash-talk recap. Everyone reads it; commissioners can generate or
 * regenerate it. Defaults to the latest completed week, with a week picker.
 */
export function RecapPage() {
  const { stored } = useOutletContext<SeasonContext>();
  const me = useMe();
  const season = stored.bundle.data.season;

  const completed = useMemo(
    () => [...stored.bundle.data.completedWeeks].sort((a, b) => b - a),
    [stored],
  );
  // A notification links to ?week=N; without it, fall back to the latest week.
  const [params] = useSearchParams();
  const linkedWeek = Number(params.get('week'));
  const [week, setWeek] = useState<number | null>(
    completed.includes(linkedWeek) ? linkedWeek : (completed[0] ?? null),
  );
  const [recap, setRecap] = useState<Recap | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setWeek((w) => (w != null && completed.includes(w) ? w : completed[0] ?? null));
  }, [completed]);

  // Tapping a second notification while the app is already open changes the
  // query string without remounting, so follow it rather than only seeding from
  // it on first render.
  useEffect(() => {
    if (completed.includes(linkedWeek)) setWeek(linkedWeek);
  }, [linkedWeek, completed]);

  useEffect(() => {
    if (week == null) return;
    setLoading(true);
    setError(null);
    fetchRecap(season, week)
      .then(setRecap)
      .catch(() => setRecap(null))
      .finally(() => setLoading(false));
  }, [season, week]);

  async function handleGenerate() {
    if (week == null) return;
    setBusy(true);
    setError(null);
    try {
      setRecap(await generateRecap(season, week));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not generate');
    } finally {
      setBusy(false);
    }
  }

  if (completed.length === 0) {
    return (
      <div className="space-y-4">
        <Header season={season} />
        <p className="text-sm text-chalk-dim">No completed weeks to recap yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Header season={season} />

      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2">
          <span className="eyebrow">Week</span>
          <select
            value={week ?? ''}
            onChange={(e) => setWeek(Number(e.target.value))}
            className="rounded-md border border-felt-line bg-felt-raised px-2 py-1 font-mono text-sm text-chalk"
          >
            {completed.map((w) => (
              <option key={w} value={w}>
                {w}
              </option>
            ))}
          </select>
        </label>
        {me?.isCommissioner && (
          <button
            onClick={handleGenerate}
            disabled={busy}
            className="rounded-md bg-brass px-3 py-1.5 font-display text-sm font-bold text-chalk transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            {busy ? 'Writing…' : recap ? 'Regenerate' : 'Generate recap'}
          </button>
        )}
      </div>

      {error && <p className="text-sm text-blood">{error}</p>}

      {loading ? (
        <p className="animate-pulse font-mono text-sm text-chalk-dim">loading…</p>
      ) : recap ? (
        <article className="card p-6">
          {/* The one genuinely long-form thing in the app. Everything else here
              is a table or a card and wants the width; a paragraph does not —
              unconstrained it ran the full container, which was already too far
              at 5xl and further once the shell grew. */}
          <p className="max-w-prose whitespace-pre-wrap font-editorial text-lg leading-relaxed text-chalk">
            {recap.text}
          </p>
          <p className="mt-4 font-mono text-[0.7rem] text-chalk-dim">
            {new Date(recap.generatedAt).toLocaleDateString()}
          </p>
        </article>
      ) : (
        <p className="text-sm text-chalk-dim">
          No recap for Week {week} yet.
          {me?.isCommissioner ? ' Hit Generate to write one.' : ''}
        </p>
      )}
    </div>
  );
}

function Header({ season }: { season: number }) {
  return (
    <div>
      <p className="eyebrow">{season} · the weekly word</p>
      <h1 className="mt-1 font-display text-3xl font-black uppercase tracking-tight">Recap</h1>
    </div>
  );
}
